import { canonicalizeBackOfficeId } from "@kkp/api-client";
import type {
  BulkStatusRequest,
  Company,
  CompanyBulkStatusResult,
  CompanyCreateRequest,
  CompanyExportResult,
  CompanyList,
  CompanyStatus,
  CompanyStatusChange,
  CompanyStatusRequest,
  CompanyUpdateRequest,
  CreateCompanyResponse,
  ExportRequest,
  PicReasonRequest,
  PicAccess,
  PicBrowsersRevokeResult,
  ResendInviteResponse,
} from "@kkp/api-client";
import { z } from "zod";
import { apiRequest } from "./client";

// Company / CompanyStatus are owned by @kkp/api-client; re-export so existing
// `../api/companies` importers keep working without a second definition.
export type { Company, CompanyStatus, CreateCompanyResponse };

// Runtime validation for the core company fields the BFFE returns.
export const CompanySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(["Active", "Inactive"]),
  updatedAt: z.string(),
}) satisfies z.ZodType<Company>;

export async function listCompanies(): Promise<Company[]> {
  const data = await apiRequest<CompanyList>({
    path: "/companies",
    method: "GET",
  });
  return z.object({ items: z.array(CompanySchema) }).parse(data).items;
}

export async function createCompany(
  body: CompanyCreateRequest,
): Promise<CreateCompanyResponse> {
  return apiRequest<CreateCompanyResponse, CompanyCreateRequest>({
    path: "/companies",
    method: "POST",
    body: {
      ...body,
      backOfficeId: canonicalizeBackOfficeId(body.backOfficeId ?? ""),
    },
  });
}

export async function updateCompany(
  id: string,
  body: CompanyUpdateRequest,
): Promise<Company> {
  const payload =
    typeof body.backOfficeId === "string"
      ? {
          ...body,
          backOfficeId: canonicalizeBackOfficeId(body.backOfficeId),
        }
      : body;

  return apiRequest<Company, CompanyUpdateRequest>({
    path: `/companies/${id}`,
    method: "PATCH",
    body: payload,
  });
}

export async function updateCompanyStatus(
  id: string,
  status: CompanyStatus,
): Promise<CompanyStatusChange> {
  return apiRequest<CompanyStatusChange, CompanyStatusRequest>({
    path: `/companies/${id}/status`,
    method: "PATCH",
    body: { status },
  });
}

export async function bulkUpdateCompanyStatus(
  ids: string[],
  status: CompanyStatus,
): Promise<CompanyBulkStatusResult> {
  return apiRequest<CompanyBulkStatusResult, BulkStatusRequest>({
    path: "/companies/bulk/status",
    method: "POST",
    body: { ids, status },
  });
}

export async function exportCompanies(
  ids: string[],
): Promise<CompanyExportResult> {
  return apiRequest<CompanyExportResult, ExportRequest>({
    path: "/companies/export",
    method: "POST",
    body: { ids },
  });
}

/**
 * Resend the PIC invite email for a company.
 *
 * The server is the authority on whether the invite is still needed: if the
 * PIC user is already active it returns `{ sent: false, warning }` instead
 * of an error, so the UI does not need to check the PIC status itself.
 */
export async function resendCompanyInvite(
  id: string,
): Promise<ResendInviteResponse> {
  return apiRequest<ResendInviteResponse>({
    path: `/companies/${id}/invite/resend`,
    method: "POST",
  });
}

export function getPicAccess(companyId: string): Promise<PicAccess> {
  return apiRequest<PicAccess>({
    path: `/companies/${companyId}/pic/access`,
    method: "GET",
  });
}

export function revokePicBrowsers(
  companyId: string,
  reason: string,
): Promise<PicBrowsersRevokeResult> {
  return apiRequest<PicBrowsersRevokeResult, PicReasonRequest>({
    path: `/companies/${companyId}/pic/browsers/revoke`,
    method: "POST",
    body: { reason },
  });
}

export function suspendPicAccess(
  companyId: string,
  reason: string,
): Promise<void> {
  return apiRequest<void, PicReasonRequest>({
    path: `/companies/${companyId}/pic/suspend`,
    method: "POST",
    body: { reason },
  });
}

export function restorePicAccess(
  companyId: string,
  reason: string,
): Promise<void> {
  return apiRequest<void, PicReasonRequest>({
    path: `/companies/${companyId}/pic/restore`,
    method: "POST",
    body: { reason },
  });
}
